/**
 * <LiveStatusBadges> — les badges d'une séance (molecule).
 *
 * **Un seul badge d'état, et au plus un badge de contexte** : ce que la règle d'affichage a retenu
 * pour cette séance (`sessionBadges`, lue par `useSessionBadges`). La molécule ne choisit rien —
 * elle peint, dans l'ordre : l'état (« Terminé », « Paiement en attente », « Absence signalée »,
 * « En cours », « Aujourd'hui »), le décompte du jour même (« dans 1 h 42 »), puis le contexte
 * (« Retard », « Remplaçant », « Rattrapage », « Replanifié »), discret.
 *
 * La table libellé ↔ visage ↔ glyphe (`LIVE_STATUS_CHIPS`) est interne à ce dossier : toute surface
 * qui montre une séance — la carte de la prochaine séance, les lignes — écrit donc le même mot,
 * au même poids. Un appelant choisit la séance, jamais le libellé ni la couleur.
 *
 * Elle ne pose pas sa rangée : l'appelant l'enveloppe, et ne la monte que s'il y a des badges
 * (`badges` non nul) — une rangée vide prendrait son interligne.
 */
import React from "react"

import { Badge } from "../../atoms/Badge"
import { Text } from "../../atoms/typography/Text"
import { type UILiveSessionBadges } from "../../../ports/primary/ui/types"
import { LIVE_STATUS_CHIPS } from "./liveStatusChips"

export type LiveStatusBadgesProps = {
    /** Les badges retenus pour la séance — `sessionBadges`, par `useSessionBadges`. */
    badges: UILiveSessionBadges
    testID?: string
}

export function LiveStatusBadges(props: LiveStatusBadgesProps) {
    const { badges, testID } = props
    const { state, countdown, context } = badges

    return (
        <>
            {state != null && <StatusChip badge={state} testID={testID} />}
            {countdown != null && (
                <Text size="text-sm" weight="bold" color="text-primary">
                    {countdown}
                </Text>
            )}
            {context != null && <StatusChip badge={context} testID={testID} />}
        </>
    )
}

function StatusChip(props: { badge: keyof typeof LIVE_STATUS_CHIPS; testID?: string }) {
    const chip = LIVE_STATUS_CHIPS[props.badge]
    return (
        <Badge
            testID={props.testID}
            text={chip.label}
            variant={chip.variant}
            tinted={chip.tinted}
            iconName={chip.icon}
        />
    )
}
